import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { propTypes } from '../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Button, IconClose, IconArrowHead, ResponsiveImage, IconDownload } from '../';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';
import ImageGallery from 'react-image-gallery';
import FullscreenButton from '../FullscreenButton/FullscreenButton';

import css from './TransactionImageGallery.module.css';

const TransactionImageGallery = props => {
  const { intl, images, customOnScreenChange } = props;

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const galleryRef = useRef(null);
  const activeDownloadButtonRef = useRef(null);

  useEffect(() => {
    if (isFullscreen && galleryRef.current) {
      galleryRef.current.slideToIndex(selectedIndex);
    }
  }, [isFullscreen, images, selectedIndex]);

  const items = images.map((img, i) => {
    return {
      key: img.key,
      original: img.url,
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      name: img?.name,
      thumbnail: img.url,
      image: img.url,
    };
  });

  const downloadImage = async () => {
    try {
      const currentIndex = galleryRef.current?.getCurrentIndex?.();
      const currentItem = items?.[currentIndex];

      if (!currentItem?.original) return;

      const response = await fetch(currentItem.original, { mode: 'cors' });
      if (!response.ok) throw new Error('Network error');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = currentItem.name || 'image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const renderItem = item => {
    // Open fullscreen by clicking on image
    const onImageClick = () => {
      const index = images?.findIndex(img => img.key === item.key);

      if (index >= 0) {
        setSelectedIndex(index);
        customOnScreenChange(index);
        // galleryRef?.current?.fullScreen();
      }
    };

    return (
      <div
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        onClick={onImageClick}
      >
        {isFullscreen && (
          <button
            ref={el => {
              if (el) activeDownloadButtonRef.current = el;
            }}
            style={{ visibility: 'hidden' }}
            className={css.downloadBtn}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              return downloadImage();
            }}
          >
            <IconDownload className={css.downloadIcon} />
            <FormattedMessage id="SendMessageForm.downloadPictureButton" />
          </button>
        )}
        <div className={css.itemContainer}>
          <div
            className={classNames(css.item, { [css.itemFullscreen]: isFullscreen })}
            style={{ backgroundImage: `url(${item?.image})` }}
          />
        </div>
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button
        className={css.navLeft}
        disabled={disabled}
        onClick={e => {
          e.preventDefault();
          onClick();
        }}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };

  const renderRightNav = (onClick, disabled) => {
    return (
      <button
        className={css.navRight}
        disabled={disabled}
        onClick={e => {
          e.preventDefault();
          onClick();
        }}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };

  const renderFullscreenButton = (onClick, isFullscreen = true) => {
    if (isFullscreen) {
      return (
        <>
          <button
            className={css.downloadBtn}
            onClick={() => {
              activeDownloadButtonRef.current?.click();
            }}
          >
            <IconDownload className={css.downloadIcon} />
            <FormattedMessage id="SendMessageForm.downloadPictureButton" />
          </button>
          <Button
            onClick={onClick}
            rootClassName={css.close}
            title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
          >
            <IconClose rootClassName={css.closeIcon} />
          </Button>
        </>
      );
    }

    return null;
  };

  const renderOpenFullscreenButton = () => <FullscreenButton onClick={customOnScreenChange} />;

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  return (
    <div className={css.galleryContainer}>
      <p className={css.transactionExplanationMessage}>
        {intl.formatMessage({ id: 'ListingImageGallery.previewProductsText' })}
      </p>
      <ImageGallery
        ref={galleryRef}
        items={items}
        renderItem={renderItem}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        showPlayButton={false}
        onScreenChange={onScreenChange}
        renderFullscreenButton={
          isFullscreen
            ? renderFullscreenButton
            : customOnScreenChange
            ? renderOpenFullscreenButton
            : undefined
        }
        slideToIndex={3}
        renderThumbInner={image => (
          <div
            key={image?.thumbnail}
            className={css.galleryThumbnail}
            style={{ backgroundImage: `url(${image?.thumbnail})` }}
          ></div>
        )}
      />
    </div>
  );
};

TransactionImageGallery.defaultProps = {
  rootClassName: null,
  className: null,
  thumbnailVariants: null,
};

const { string, arrayOf } = PropTypes;

TransactionImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(TransactionImageGallery);
